import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import styled from 'styled-components';
import { Button } from '@arcflight/tf-component-library';
import { DashboardState } from '../../models';
import { Aircraft } from '../../models/aircraft';
import RadioInputButton from '../../components/RadioInputButton/RadioInputButton';
import { changeDrawerVisibility, setDrawerChanges } from '../../models/drawer';
import { add } from '../../models/userSettings/actions';
import TFRadioInput from '../../components/TFRadioInput/TFRadioInput';

interface LogbookDrawerProps {
  selectedAircraft: Aircraft[];
  setSelectedAircraft: (aircraft: Aircraft[]) => void;
  checkedColumns: string[];
  setCheckedColumns: (columns: string[]) => void;
  selectableColumns: string[];
  passedAircraft: Aircraft[];
}

interface LogbookColumn {
  title: string;
  checked: boolean;
}

const fixedColumns = ['trip_date', 'departure_airport', 'arrival_airport'];

const columnOrder = [
  'trip_date',
  'departure_airport',
  'arrival_airport',
  'trip_number',
  'ac_registration',
  'blocks_off',
  'takeoff',
  'landing',
  'blocks_on',
  'flight_time',
  'blocks_time',
  'captain',
  'first_officer',
  'landings',
  'airframe_hours',
  'engine_1_hours',
  'engine_2_hours',
  'engine_3_hours',
  'engine_4_hours',
  'engine_1_cycles',
  'engine_2_cycles',
  'engine_3_cycles',
  'engine_4_cycles',
  'apu_hours',
  'apu_cycles',
  'prop_1_hours',
  'prop_2_hours',
  'prop_3_hours',
  'prop_4_hours',
  'airframe_landings',
  'airframe_cycles',
  'hobbs_arrival',
  'hobbs_departure',
  'flight_hobbs',
  'passengers_male',
  'passengers_female',
  'passengers_children',
  'passengers_infants',
  'total_passengers',
  'engine_1_oil_uplift',
  'engine_2_oil_uplift',
  'engine_3_oil_uplift',
  'engine_4_oil_uplift',
  'initial_fuel',
  'adjusted_initial_fuel',
  'total_fuel_truck_uplift',
  'total_aircraft_uplift',
  'fuel_adjustment',
  'fuel_adjustment_justification',
  'departure_fuel',
  'arrival_fuel',
  'planned_fuel_burn',
  'actual_fuel_burn',
  'srp_number',
  'flight_source',
];

const DrawerWrapper = styled.div`
  padding: 32px 48px;
  @media (max-width: 450px) {
    padding: 24px;
  }
`;

const Header = styled.div`
  font-weight: 500;
  color: ${({ theme }): string => theme.colors.black};
  margin: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OptionsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 8px;
  row-gap: 8px;
  margin-top: ${({ marginTop }): string => (marginTop ? `${marginTop}` : '0')};
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 24px;
`;

const SaveButton = styled.div`
  margin-right: 12px;
`;

const LogbookDrawer: React.FC<LogbookDrawerProps> = ({
  selectedAircraft,
  setSelectedAircraft,
  checkedColumns,
  setCheckedColumns,
  selectableColumns,
  passedAircraft,
}) => {
  const {
    userSettings: { logbook_columns },
  } = useSelector((state: DashboardState) => state);

  const dispatch = useDispatch();

  const [adaptedColumns, setAdaptedColumns] = useState<LogbookColumn[]>([]);
  const [localAircraft, setLocalAircraft] = useState<Aircraft[]>([]);
  const [localCheckedColumns, setLocalCheckedColumns] = useState<string[]>([]);
  const [selectAllAircraft, setSelectAllAircraft] = useState(null);
  const [selectAllColumns, setSelectAllColumns] = useState(null);

  const handleAircraftClick = (aircraft: Aircraft): void => {
    const isSelected = localAircraft.includes(aircraft);
    const newAircraft = isSelected ? localAircraft.filter((ac) => ac.id !== aircraft.id) : [...localAircraft, aircraft];

    setLocalAircraft(newAircraft);

    let selectAllState = null;
    if (newAircraft.length === 0) {
      selectAllState = 'none';
    } else if (newAircraft.length === passedAircraft.length) {
      selectAllState = 'all';
    }

    setSelectAllAircraft(selectAllState);
    dispatch(setDrawerChanges({ payload: true }));
  };

  const handleColumnSelection = (title: string): void => {
    const isSelected = localCheckedColumns.includes(title);
    const newColumns = isSelected
      ? localCheckedColumns.filter(col => col !== title)
      : [...localCheckedColumns, title];

    setLocalCheckedColumns(newColumns);

    let selectAllState = null;
    if (newColumns.length === 0) {
      selectAllState = 'none';
    } else if (newColumns.length === adaptedColumns.length) {
      selectAllState = 'all';
    }

    setSelectAllColumns(selectAllState);
    dispatch(setDrawerChanges({ payload: true }));
  };

  const alphabeticalSort = (a, b): number => {
    if (Array.isArray(a)) {
      const aReg = a[1]?.registration;
      const bReg = b[1]?.registration;
      if (aReg === bReg) {
        return 0;
      }
      return aReg < bReg ? -1 : 1;
    }
    if (a?.title === b?.title) {
      return 0;
    }
    return a?.title < b?.title ? -1 : 1;
  };

  const buildAircraft = (): ReactElement[] => {
    return passedAircraft
      .sort(alphabeticalSort)
      .map((ac) => {
        return (
          <RadioInputButton
            key={ac.id}
            text={ac.registration}
            active={localAircraft.filter((air) => air.id === ac.id).length > 0}
            checkbox
            marginRight="8px"
            marginBottom="8px"
            onClick={(): void => handleAircraftClick(ac)}
          />
        );
      });
  };

  const buildColumns = (): ReactElement[] => {
    const columnOptions = adaptedColumns
      ?.filter((item) => {
        if (fixedColumns?.includes(item.title)) return null;
        return item;
      })
      .sort(alphabeticalSort)
      .map((column) => {
        let newTitle = column.title.replace(/_/g, ' ');
        if (column.title === 'airframe_hours_original') newTitle = 'airframe hours';
        if (column.title === 'airframe_landings') newTitle = 'total landings';
        if (column.title === 'ac_registration') newTitle = 'registration';
        if (column.title.endsWith('uplift')) newTitle = newTitle.replace(' uplift', '');
        newTitle = newTitle.charAt(0).toUpperCase() + newTitle.slice(1);
        return (
          <RadioInputButton
            key={column.title}
            text={newTitle}
            active={localCheckedColumns.includes(column.title)}
            checkbox
            marginRight="8px"
            marginBottom="8px"
            onClick={(): void => handleColumnSelection(column.title)}
          />
        );
      });
    return columnOptions;
  };

  const handleSave = (): void => {
    setCheckedColumns(localCheckedColumns);
    setSelectedAircraft(localAircraft);
    if (!_.isEqual(checkedColumns, localCheckedColumns)) {
      const formData = new FormData();
      formData.append('logbook_columns', JSON.stringify(localCheckedColumns));
      dispatch(add({ payload: { form: formData } }));
    }
    dispatch(changeDrawerVisibility({ payload: false }));
  };

  const closeDrawer = (): void => {
    dispatch(changeDrawerVisibility({ payload: false }));
  };

  const selectAllButton = (type: string) => {
    const onChange = (value: string): void => {
      const selectAll = value === 'all';

      if (type === 'aircraft') {
        setLocalAircraft(selectAll ? passedAircraft : []);
        setSelectAllAircraft(selectAll ? 'all' : 'none');
      } else {
        setLocalCheckedColumns(selectAll ? adaptedColumns.map((col) => col.title) : []);
        setSelectAllColumns(selectAll ? 'all' : 'none');
      }
    };

    return (
      <TFRadioInput
        options={[{ title: 'Select all', value: 'all' }, { title: 'Deselect all', value: 'none' }]}
        onChange={onChange}
        value={type === 'aircraft' ? selectAllAircraft : selectAllColumns}
      />
    )
  }

  useEffect(() => {
    if (selectableColumns) {
      const newColumns = selectableColumns
        .filter((col) => columnOrder.indexOf(col) !== -1)
        .sort((a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b))
        .map((item) => {
          const isChecked = logbook_columns.includes(item);
          return { title: item, checked: isChecked };
        });
      setAdaptedColumns(newColumns);
    }
  }, [logbook_columns, selectableColumns]);

  useEffect(() => {
    if (selectedAircraft.length > 0) {
      setLocalAircraft(selectedAircraft);
      if (selectedAircraft.length === passedAircraft.length) {
        setSelectAllAircraft('all');
      }
    }
  }, [selectedAircraft, passedAircraft.length]);

  useEffect(() => {
    if (checkedColumns.length > 0) {
      setLocalCheckedColumns(checkedColumns);
      if (checkedColumns.length === adaptedColumns.length) {
        setSelectAllColumns('all');
      }
    }
  }, [checkedColumns, adaptedColumns.length]);

  return (
    <DrawerWrapper>
      <Header>Aircraft {selectAllButton('aircraft')}</Header>
      <OptionsWrapper>{buildAircraft()}</OptionsWrapper>
      <Header>Columns {selectAllButton('columns')}</Header>
      <OptionsWrapper>{buildColumns()}</OptionsWrapper>
      <ButtonWrapper>
        <SaveButton>
          <Button height="32px" onClick={(): void => handleSave()}>
            Save
          </Button>
        </SaveButton>
        <Button primary={false} height="32px" onClick={closeDrawer}>
          Cancel
        </Button>
      </ButtonWrapper>
    </DrawerWrapper>
  );
};

export default LogbookDrawer;
